
import { motion, AnimatePresence } from 'framer-motion';
import { useBigFiveTest } from '@/hooks/useBigFiveTest';
import { BigFiveQuestion, TestVersion } from '@/types/personality';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { processBigFiveAnswers } from '@/utils/bigFiveScoring';

import QuestionCard from './QuestionCard';
import ProgressBar from './ProgressBar';
import PreviousResultsButton from './PreviousResultsButton';

interface BigFiveTestInterfaceProps {
  version: TestVersion;
  onTestComplete: (results: any, resultId: string | null) => void;
}

export default function BigFiveTestInterface({ version, onTestComplete }: BigFiveTestInterfaceProps) {
  const { toast } = useToast();
  
  const {
    currentQuestion,
    currentIndex,
    questions,
    answers,
    progress,
    answerQuestion,
    previousQuestion,
    getCurrentAnswer
  } = useBigFiveTest({
    version: version,
  });

  const saveResultsToDatabase = async (results: any) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        console.warn('Usuário não autenticado, não salvando resultados');
        return null;
      }

      const { data, error } = await supabase
        .from('big_five_results')
        .insert({
          user_id: session.user.id,
          openness: results.scores.O,
          conscientiousness: results.scores.C,
          extraversion: results.scores.E,
          agreeableness: results.scores.A,
          neuroticism: results.scores.N
        })
        .select()
        .single();

      if (error) {
        console.error('Erro ao salvar resultados:', error);
        return null;
      }

      if (!data) {
        console.error('No data returned from insert operation');
        return null;
      }

      return data.id;
    } catch (error) {
      console.error('Erro ao salvar resultados:', error);
      return null;
    }
  };
  
  const handleAnswer = async (score: number) => {
    answerQuestion(score);
    
    if (currentIndex === questions.length - 1) {
      const userId = localStorage.getItem('userEmail') || 'anonymous';
      const results = processBigFiveAnswers(userId, [...answers, { questionId: currentQuestion!.id, score }], questions);
      
      const resultId = await saveResultsToDatabase(results);
      
      onTestComplete(results, resultId);
      
      toast({
        title: "Teste concluído!",
        description: "Seus resultados estão prontos para análise.",
      });
    }
  };
  
  const handleSaveAndExit = () => {
    toast({
      title: "Progresso salvo",
      description: "Você pode continuar o teste mais tarde.",
    });
    window.location.href = "/app";
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Teste de Personalidade Big Five</h1>
        <div className="flex gap-2">
          <PreviousResultsButton />
          <Button variant="outline" onClick={handleSaveAndExit}>
            Salvar e Continuar Depois
          </Button>
        </div>
      </div>
      
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm">
          Questão {currentIndex + 1} de {questions.length}
        </span>
        <span className="text-sm">{progress}% completo</span>
      </div>
      
      <ProgressBar progress={progress} className="mb-8" />
      
      <AnimatePresence mode="wait">
        <QuestionCard
          key={currentQuestion?.id}
          question={currentQuestion as BigFiveQuestion}
          currentAnswer={getCurrentAnswer()}
          onAnswer={handleAnswer}
          onBack={currentIndex > 0 ? previousQuestion : undefined}
          isFirst={currentIndex === 0}
        />
      </AnimatePresence>
    </div>
  );
}
